import { useMemo } from 'react';
import { Modal, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { Bell, CheckCheck, PackagePlus, X } from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown, FadeOut } from 'react-native-reanimated';

import { colors, radius, typography } from '@/theme/tokens';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { notificacionesPara, marcarLeida, marcarTodasLeidas, type Notificacion } from '@/services/firebase/notificaciones';
import { useAuth } from '@/stores/authStore';
import { useNav } from '@/stores/navStore';
import { formatDateTime } from '@/utils/format';

interface Props {
  visible: boolean;
  onClose: () => void;
}

export function NotificacionesPanel({ visible, onClose }: Props) {
  const { width } = useWindowDimensions();
  const wide = width >= 920;
  const perfil = useAuth((s) => s.perfil);
  const navigate = useNav((s) => s.setCurrent);

  const q = useMemo(() => (perfil ? notificacionesPara(perfil.rol) : null), [perfil]);
  const { data, loading } = useFirestoreCollection(q);
  const noLeidas = data.filter((n) => !n.leida);

  const onSelect = (n: Notificacion) => {
    marcarLeida(n.id).catch(() => {});
    if (n.tipo === 'reponer_producto' && n.origen) {
      navigate(n.origen === 'venta' ? 'ProductosVenta' : 'InsumosInternos');
      onClose();
    }
  };

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Animated.View
        entering={FadeIn.duration(160)}
        exiting={FadeOut.duration(160)}
        style={{
          flex: 1,
          backgroundColor: 'rgba(15,23,42,0.4)',
          flexDirection: wide ? 'row' : 'column',
          justifyContent: wide ? 'flex-end' : 'flex-start',
        }}
      >
        <Pressable style={{ position: 'absolute', inset: 0 }} onPress={onClose} />
        <Animated.View
          entering={FadeInDown.duration(220)}
          style={{
            width: wide ? 420 : '100%',
            maxHeight: wide ? '100%' : '85%',
            backgroundColor: colors.white,
            borderTopLeftRadius: wide ? 0 : radius['4xl'],
            borderTopRightRadius: wide ? 0 : radius['4xl'],
            // @ts-ignore web only
            boxShadow: '-8px 0 32px -12px rgba(15,23,42,0.25)',
            height: wide ? '100%' : undefined,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              padding: 18,
              borderBottomWidth: 1,
              borderBottomColor: colors.ink[100],
            }}
          >
            <Bell size={20} color={colors.magenta[700]} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
                Notificaciones
              </Text>
              <Text style={{ fontSize: 12, color: colors.ink[500] }}>
                {noLeidas.length} sin leer · {data.length} en total
              </Text>
            </View>
            {noLeidas.length > 0 ? (
              <Pressable
                onPress={() => marcarTodasLeidas(data).catch(() => {})}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: 10,
                  height: 30,
                  borderRadius: 999,
                  backgroundColor: hovered ? colors.magenta[100] : colors.magenta[50],
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <CheckCheck size={13} color={colors.magenta[700]} />
                <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>
                  Marcar todas
                </Text>
              </Pressable>
            ) : null}
            <Pressable
              onPress={onClose}
              style={({ hovered }) => ({
                width: 30,
                height: 30,
                borderRadius: 10,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.ink[100] : 'transparent',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <X size={16} color={colors.ink[600]} />
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ padding: 12, gap: 6 }}>
            {loading ? (
              <Text style={{ color: colors.ink[500], padding: 14 }}>Cargando…</Text>
            ) : data.length === 0 ? (
              <View style={{ alignItems: 'center', paddingVertical: 60, gap: 8 }}>
                <View
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 28,
                    backgroundColor: colors.crema[100],
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Bell size={26} color={colors.ink[400]} />
                </View>
                <Text
                  style={{
                    fontFamily: typography.display.fontFamily,
                    fontSize: 16,
                    fontWeight: typography.weights.bold,
                    color: colors.ink[900],
                  }}
                >
                  Todo tranquilo
                </Text>
                <Text style={{ color: colors.ink[500], fontSize: 13, textAlign: 'center' }}>
                  No tenés notificaciones por el momento.
                </Text>
              </View>
            ) : (
              data.map((n) => <NotifRow key={n.id} n={n} onPress={() => onSelect(n)} />)
            )}
          </ScrollView>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}

function NotifRow({ n, onPress }: { n: Notificacion; onPress: () => void }) {
  const Icon = n.tipo === 'reponer_producto' ? PackagePlus : Bell;
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        padding: 14,
        borderRadius: radius.xl,
        backgroundColor: !n.leida ? colors.magenta[50] : hovered ? colors.crema[50] : 'transparent',
        borderWidth: 1,
        borderColor: !n.leida ? colors.magenta[100] : 'transparent',
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          backgroundColor: n.tipo === 'reponer_producto' ? colors.warning[50] : colors.magenta[100],
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={18} color={n.tipo === 'reponer_producto' ? colors.warning[600] : colors.magenta[700]} />
      </View>
      <View style={{ flex: 1, gap: 4 }}>
        <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 14 }} numberOfLines={2}>
          {n.titulo}
        </Text>
        <Text style={{ color: colors.ink[600], fontSize: 12, lineHeight: 17 }} numberOfLines={3}>
          {n.mensaje}
        </Text>
        <Text style={{ color: colors.ink[400], fontSize: 11 }}>
          {formatDateTime(n.creadaEn?.toDate?.() ?? null)}
        </Text>
      </View>
      {!n.leida ? (
        <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.magenta[700], marginTop: 6 }} />
      ) : null}
    </Pressable>
  );
}
